import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Crown, Check } from "lucide-react";

export default function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id");

  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    let count = 0;

    const poll = async () => {
      if (cancelled) return;
      count += 1;
      setAttempts(count);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          try {
            const { data: me } = await api.get("/auth/me");
            setUser(me);
          } catch (e) { console.warn("[payment-success] /auth/me refresh failed", e); }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (e) { console.warn("[payment-success] status poll failed", e); }
      if (count >= 15) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, setUser]);

  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Navbar />
      <main className="max-w-md mx-auto px-4 py-16">
        <Card className="p-8 rounded-3xl border-[#E8DEC8] bg-white text-center" data-testid="payment-success-card">
          {status === "polling" && (
            <>
              <div className="w-12 h-12 mx-auto border-4 border-[#D95D39] border-t-transparent rounded-full animate-spin mb-4" />
              <h1 className="font-serif text-2xl font-bold text-[#2C2623] mb-2">Confirming your payment…</h1>
              <p className="text-sm text-[#6E655F]">Give us a few seconds ({attempts}s).</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-[#FFF0EC] flex items-center justify-center mb-3">
                <Crown className="w-7 h-7 text-[#B45309]" />
              </div>
              <h1 className="font-serif text-3xl font-bold text-[#2C2623] mb-2">Welcome to Premium 🎉</h1>
              <p className="text-[#6E655F] mb-6">
                Enjoy unlimited likes, see who liked you, advanced filters, and your monthly boost.
              </p>
              <div className="flex flex-col gap-2">
                <Button onClick={() => navigate("/discover")}
                  data-testid="payment-success-discover-btn"
                  className="rounded-full bg-[#D95D39] hover:bg-[#C24C29] h-11">
                  Start discovering <Check className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" onClick={() => navigate("/premium")}
                  data-testid="payment-success-manage-btn"
                  className="rounded-full h-11">Manage subscription</Button>
              </div>
            </>
          )}
          {status === "failed" && (
            <>
              <h1 className="font-serif text-2xl font-bold text-[#2C2623] mb-2">Payment didn't complete</h1>
              <p className="text-sm text-[#6E655F] mb-4">We could not confirm a completed payment. Check your payment history before trying again.</p>
              <Button onClick={() => navigate("/premium")} data-testid="payment-retry-btn"
                className="rounded-full bg-[#D95D39] hover:bg-[#C24C29]">Back to Premium</Button>
            </>
          )}
          {status === "timeout" && (
            <>
              <h1 className="font-serif text-2xl font-bold text-[#2C2623] mb-2">Still processing…</h1>
              <p className="text-sm text-[#6E655F] mb-4">
                Your payment may take a moment. Check /premium in a minute to confirm.
              </p>
              <Button onClick={() => navigate("/premium")} data-testid="payment-timeout-btn"
                className="rounded-full bg-[#D95D39] hover:bg-[#C24C29]">Check status</Button>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}
